import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
interface FaqItem {
  questionKey: string;
  answer: string | React.ReactNode;
  answerKey?: string;
}
const faqs: FaqItem[] = [{
  questionKey: 'faq.q1',
  answer: <>
        We deliver throughout King and Pierce counties — Bellevue, Kirkland,
        Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma,
        Puyallup, Bonney Lake, and beyond.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-778" data-mp="2042n8ogdx7k4">
          View our full delivery zone map →
        </Link>
      </>
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2',
  answer: ''
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3',
  answer: ''
}, {
  questionKey: 'faq.q4',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-779" data-mp="js5ngqceorfb">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-780" data-mp="1apsu2tf75j9a">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5',
  answer: ''
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6',
  answer: ''
}, {
  questionKey: 'faq.q7',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-619-3742" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-781" data-mp="wvt63opp2m09">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-782" data-mp="2pmdci2c23cra">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
}];
export function FAQ() {
  const t = useT();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lastClosedIndex, setLastClosedIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    if (openIndex === index) {
      // Closing this question — mark it as last closed
      setLastClosedIndex(index);
      setOpenIndex(null);
    } else {
      // Opening a new question — clear last closed
      setLastClosedIndex(null);
      setOpenIndex(index);
    }
  };
  return <section className="py-16 sm:py-24 dark:bg-[#202020] transition-colors duration-300 scroll-mt-32 bg-[#ffffff]" id="faq" data-id="element-783" data-mp="2umhtdtgvm9i1">
      {/* Header */}
      <div className="mx-auto mb-12 max-w-5xl px-4 sm:mb-20 sm:px-6" data-id="element-784" data-mp="1n621caaefeed">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-785" data-mp="1j5rxb3662y25">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-786" data-mp="2qdn09ketpr7">
            <h2 className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-stone-900 dark:text-white md:text-6xl lg:text-7xl" data-id="element-787" data-mp="7nnjg967v1oj">
              Da <span className="font-black text-[#04A05C]" data-id="element-788" data-mp="hfjmlyn5thzm">FAQs</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-789" data-mp="mduuw4xt3kfq">— Get answers fast</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-790" data-mp="qgrjcca12arl">
            {t('faq.subtitle')}
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-791" data-mp="2s960va8ntd7h">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        const answerContent = faq.answerKey ? t(faq.answerKey) : faq.answer;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#00754A]' : 'bg-transparent'}`} data-id="element-792" data-mp="21y2ica45iv5n" data-mpi={index}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-793" data-mp="1j4oxixu5k53r">
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-6 py-8 sm:py-12 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-794" data-mp="1e6ezmfh1mqeg">
                  <span className={`font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedIndex === index ? 'text-[#04A05C] dark:text-[#04A05C] group-hover:text-[#048036] dark:group-hover:text-[#048036]' : 'text-stone-900 dark:text-white group-hover:text-[#04A05C] dark:group-hover:text-[#04A05C]'}`} data-id="element-795" data-mp="1o4kxmlzqbce1">
                    {t(faq.questionKey)}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-1 sm:mt-2 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-796" data-mp="3bvk4oo30dsrt">
                    <Plus className="w-8 h-8 sm:w-12 sm:h-12 stroke-[1]" data-id="element-797" data-mp="3prxmxzfd9y7c" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-798" data-mp="3gtlnd7wivq2r">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${index}`} data-id="element-799" data-mp="3gsspkwfjkawr">
                      <p className="pb-12 sm:pb-16 text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-4xl font-medium" data-id="element-800" data-mp="axote1jtt686">
                        {answerContent}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-801" data-mp="3td94ei2pwwpb">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-802" data-mp="3ocbne8tg28pe">
          <MessageCircle className="w-5 h-5 text-[#04A05C] shrink-0" data-id="element-803" data-mp="3je0eq1evy48r" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-804" data-mp="3ef6yw2c4m15f">
            {t('faq.stillQuestions')}
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-805" data-mp="1hk165nt5e0zr">
              {t('faq.shootText')}
            </span>
          </p>
          <a href="sms:206-619-3742" className="inline-flex items-center justify-center px-6 py-2.5 hover:bg-[#048B3A] text-white rounded-full font-bold text-sm transition-colors bg-[#04A05C]" data-id="element-806" data-mp="1l300levvvyxt">
            {t('faq.textUs')}
          </a>
        </div>
      </div>
    </section>;
}